import { useCallback, useEffect, useState, useRef } from 'react';

import type { fnGetFilteredProductLinks } from '@atlassian/global-shortcuts';
import type { ProductLink } from '@atlassian/navigation-bff-data-provider';

import type { ShortcutItemsConfig } from '../../../common/types';

interface UseInvitedAppProductLinksArgs {
	shortcutItemsConfig: ShortcutItemsConfig;
}

export const useInvitedAppProductLinks = ({
	shortcutItemsConfig,
}: UseInvitedAppProductLinksArgs) => {
	// store the invited app product links by apsKey in a state
	const [invitedAppProductLinks, setInvitedAppProductLinks] = useState<
		Record<string, ProductLink[]>
	>({});
	const getFilteredProductLinksRef = useRef<fnGetFilteredProductLinks | undefined>(undefined);

	/**
	 * Update invited app product links when getFilteredProductLinks changes
	 */
	const handleUpdateInvitedAppProductLinks = useCallback(
		(getFilteredProductLinks: fnGetFilteredProductLinks): void => {
			if (typeof getFilteredProductLinks === 'function') {
				getFilteredProductLinksRef.current = getFilteredProductLinks;
				setInvitedAppProductLinks(
					resolveInvitedAppProductLinks(getFilteredProductLinksRef.current, shortcutItemsConfig),
				);
			}
		},
		[shortcutItemsConfig],
	);

	/**
	 * Update invited app product links when shortcutItemsConfig changes
	 */
	useEffect(() => {
		if (getFilteredProductLinksRef.current) {
			setInvitedAppProductLinks(
				resolveInvitedAppProductLinks(getFilteredProductLinksRef.current, shortcutItemsConfig),
			);
		}
	}, [shortcutItemsConfig]);

	return {
		invitedAppProductLinks,
		handleUpdateInvitedAppProductLinks,
	};
};

const resolveInvitedAppProductLinks = (
	getFilteredProductLinks: fnGetFilteredProductLinks,
	shortcutItemsConfig: ShortcutItemsConfig,
) =>
	Object.entries(shortcutItemsConfig).reduce<Record<string, ProductLink[]>>((acc, [, config]) => {
		if (config.type === 'link' && config.apsKey) {
			const { links } = getFilteredProductLinks(config.apsKey);
			acc[config.apsKey] = links;
		}
		return acc;
	}, {});
